import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { DashboardHeader, StatCard, Tabs, StatusBadge, Empty, fmtTs } from "../components/DashboardUI";
import ChangePasswordModal from "../components/ChangePasswordModal";

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [audit, setAudit] = useState([]);
  const [stats, setStats] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [b, t, s, p, n, a, st, u] = await Promise.all([
        api.get("/bookings"),
        api.get("/testimonials?all=true"),
        api.get("/services?all=true"),
        api.get("/promotions?active_only=false"),
        api.get("/admin/notifications"),
        api.get("/admin/audit"),
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setBookings(b.data); setTestimonials(t.data); setServices(s.data);
      setPromotions(p.data); setNotifications(n.data); setAudit(a.data); setStats(st.data);
      setUsers(u.data);
    } catch { nav("/admin/login"); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/admin/login");
    if (user.role !== "admin") return nav("/");
    load();
  }, [user, loading]);

  const updateStatus = async (kind, id, status) => {
    try { await api.patch(`/${kind}/${id}/status`, { status }); toast.success(`${kind.slice(0, -1)} ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (kind, id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/${kind}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">Loading…</div>;

  const tabs = [
    { k: "bookings", label: "Bookings", count: stats?.bookings?.pending },
    { k: "testimonials", label: "Testimonials", count: stats?.testimonials?.pending_admin },
    { k: "services", label: "Services", count: stats?.services?.pending },
    { k: "promotions", label: "Promotions", count: promotions.filter(p => p.status === "pending").length },
    { k: "users", label: "Users" },
    { k: "notifications", label: "Email Outbox" },
    { k: "audit", label: "Audit Log" },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="admin-dashboard">
      <DashboardHeader title="Admin · DarkWebx03" subtitle="Final approval console" user={user} onLogout={logout} onChangePassword={() => setPwdOpen(true)} />
      {stats && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Customers" value={stats.customers} />
          <StatCard label="Staff" value={stats.staff} />
          <StatCard label="Pending Bookings" value={stats.bookings.pending} accent />
          <StatCard label="Awaiting Final Approval" value={stats.testimonials.pending_admin} accent />
          <StatCard label="Pending Services" value={stats.services.pending} accent />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <Tabs current={tab} onChange={setTab} prefix="admin-tab" tabs={tabs} />
        <div className="mt-6 pb-16">
          {tab === "bookings" && <BookingsTable items={bookings} onUpdate={(id, s) => updateStatus("bookings", id, s)} onDelete={(id) => remove("bookings", id)} />}
          {tab === "testimonials" && <TestimonialsTable items={testimonials} onUpdate={(id, s) => updateStatus("testimonials", id, s)} onDelete={(id) => remove("testimonials", id)} />}
          {tab === "services" && <ServicesTable items={services} onUpdate={(id, s) => updateStatus("services", id, s)} onDelete={(id) => remove("services", id)} />}
          {tab === "promotions" && <PromotionsApproval items={promotions} onRefresh={load} />}
          {tab === "users" && <UserManagement items={users} onRefresh={load} />}
          {tab === "notifications" && <NotificationsList items={notifications} />}
          {tab === "audit" && <AuditList items={audit} />}
        </div>
      </div>
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}

function ActionButtons({ id, status, onUpdate, onDelete, kind }) {
  return (
    <div className="flex gap-2 justify-end flex-wrap">
      {status !== "approved" && <button onClick={() => onUpdate(id, "approved")} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded" data-testid={`${kind}-approve-${id}`}>Approve</button>}
      {status !== "declined" && <button onClick={() => onUpdate(id, "declined")} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded" data-testid={`${kind}-decline-${id}`}>Decline</button>}
      <button onClick={() => onDelete(id)} className="text-xs px-3 py-1.5 border border-[var(--border)] rounded" data-testid={`${kind}-delete-${id}`}>Del</button>
    </div>
  );
}

function BookingsTable({ items, onUpdate, onDelete }) {
  if (!items.length) return <Empty msg="No bookings." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Service</th><th className="text-left p-4">Date / Time</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th></tr>
        </thead>
        <tbody>
          {items.map(b => (
            <tr key={b.id} className="border-t border-[var(--border)]" data-testid={`admin-booking-${b.id}`}>
              <td className="p-4">{b.name}<br/><span className="text-xs text-[var(--muted)]">{b.phone}</span></td>
              <td className="p-4">{b.service}</td>
              <td className="p-4">{b.date} {b.time}</td>
              <td className="p-4">
                <StatusBadge s={b.status} />
                {b.cancellation && <p className="text-[10px] text-[var(--muted)] italic mt-1">{b.cancellation.reason}</p>}
              </td>
              <td className="p-4"><ActionButtons id={b.id} status={b.status} onUpdate={onUpdate} onDelete={onDelete} kind="booking" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TestimonialsTable({ items, onUpdate, onDelete }) {
  if (!items.length) return <Empty msg="No testimonials." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map(t => (
        <div key={t.id} className="bg-white border border-[var(--border)] p-6" data-testid={`admin-testimonial-${t.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-2">
              <span className="text-[10px] uppercase tracking-widest text-[var(--muted)]">Owner: {t.owner_decision || (t.owner_reviewed ? "—" : "not reviewed")}</span>
              <StatusBadge s={t.status} />
            </div>
            <span className="text-xs text-[var(--muted)]">{fmtTs(t.created_at)}</span>
          </div>
          <p className="font-display text-lg italic mb-3">"{t.edited_message || t.message}"</p>
          {t.edited_message && <p className="text-[10px] text-[var(--accent)] mb-3">Owner edited from: "{t.message}"</p>}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div><p className="font-medium">{t.name}</p><p className="text-xs text-[var(--muted)] uppercase tracking-widest">{t.role}</p></div>
            <ActionButtons id={t.id} status={t.status} onUpdate={onUpdate} onDelete={onDelete} kind="testimonial" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ServicesTable({ items, onUpdate, onDelete }) {
  if (!items.length) return <Empty msg="No services." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Service</th><th className="text-left p-4">Price</th><th className="text-left p-4">Owner</th><th className="text-left p-4">Public</th><th className="text-right p-4">Actions</th></tr>
        </thead>
        <tbody>
          {items.map(s => (
            <tr key={s.id} className="border-t border-[var(--border)]" data-testid={`admin-service-${s.id}`}>
              <td className="p-4"><p className="font-medium">{s.name}</p><p className="text-xs text-[var(--muted)] mt-1">{s.description}</p></td>
              <td className="p-4">AED {s.price_aed}<br/><span className="text-xs text-[var(--muted)]">{s.duration_min} min</span></td>
              <td className="p-4 text-xs uppercase tracking-widest">{s.owner_decision || "—"}</td>
              <td className="p-4"><StatusBadge s={s.status} /></td>
              <td className="p-4"><ActionButtons id={s.id} status={s.status} onUpdate={onUpdate} onDelete={onDelete} kind="service" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationsList({ items }) {
  if (!items.length) return <Empty msg="No notifications yet." />;
  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--muted)] italic">Email outbox is currently MOCKED — events are stored here. Plug in SendGrid/Resend keys to deliver real emails.</p>
      {items.map(n => (
        <div key={n.id} className="bg-white border border-[var(--border)] p-5" data-testid={`notif-${n.id}`}>
          <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-widest text-[var(--accent)]">{n.kind}</p>
              <p className="font-display text-lg">{n.payload?.subject || "(no subject)"}</p>
            </div>
            <p className="text-xs text-[var(--muted)]">{fmtTs(n.created_at)}</p>
          </div>
          <p className="text-sm text-[var(--text)]">{n.payload?.body}</p>
          <p className="text-[10px] text-[var(--muted)] mt-2">From: {n.from} → To: {(n.recipients || []).join(", ")}</p>
        </div>
      ))}
    </div>
  );
}

function AuditList({ items }) {
  if (!items.length) return <Empty msg="No audit entries." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">When</th><th className="text-left p-4">Actor</th><th className="text-left p-4">Action</th><th className="text-left p-4">Entity</th><th className="text-left p-4">Details</th></tr>
        </thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id} className="border-t border-[var(--border)] text-xs">
              <td className="p-4">{fmtTs(a.created_at)}</td>
              <td className="p-4">{a.actor_name} <span className="text-[var(--muted)]">({a.actor_role})</span></td>
              <td className="p-4 font-medium">{a.action}</td>
              <td className="p-4">{a.entity_type}</td>
              <td className="p-4 text-[var(--muted)]">{JSON.stringify(a.details).slice(0, 80)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromotionsApproval({ items, onRefresh }) {
  const approve = async (id) => {
    try { await api.patch(`/promotions/${id}/approve`); toast.success("Promotion approved — now live on landing page"); onRefresh?.(); }
    catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (sent to the owner):", "");
    if (reason === null) return;
    try { await api.patch(`/promotions/${id}/reject`, { reason }); toast.success("Promotion rejected"); onRefresh?.(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this promotion?")) return;
    try { await api.delete(`/promotions/${id}`); toast.success("Deleted"); onRefresh?.(); }
    catch { toast.error("Failed"); }
  };

  if (!items.length) return <Empty msg="No promotions yet." />;
  const pending = items.filter(p => p.status === "pending");
  const others = items.filter(p => p.status !== "pending");

  const Card = ({ p }) => (
    <div className="bg-white border border-[var(--border)] p-5" data-testid={`admin-promo-${p.id}`}>
      <div className="flex justify-between items-start mb-2 gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="font-display text-xl">{p.title}</p>
            <StatusBadge s={p.status} />
          </div>
          <p className="text-xs text-[var(--muted)] mb-1">Submitted by {p.created_by || "—"} ({p.created_by_role || "—"})</p>
          {p.description && <p className="text-sm text-[var(--muted)]">{p.description}</p>}
          {p.rejection_reason && <p className="text-xs text-red-600 italic mt-1">Rejection: "{p.rejection_reason}"</p>}
        </div>
        <div className="text-right">
          {p.discount_percent > 0 && <p className="font-display text-3xl text-[var(--accent)]">{p.discount_percent}%</p>}
          {p.discount_aed > 0 && <p className="font-display text-2xl text-[var(--accent)]">-{p.discount_aed}</p>}
        </div>
      </div>
      {p.valid_until && <p className="text-[10px] uppercase tracking-widest text-[var(--muted)]">Until {p.valid_until}</p>}
      <div className="flex justify-end gap-2 mt-3 flex-wrap">
        {p.status !== "approved" && (
          <button onClick={() => approve(p.id)} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700" data-testid={`admin-promo-approve-${p.id}`}>Approve</button>
        )}
        {p.status !== "rejected" && (
          <button onClick={() => reject(p.id)} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700" data-testid={`admin-promo-reject-${p.id}`}>Reject</button>
        )}
        <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 rounded hover:bg-red-50" data-testid={`admin-promo-delete-${p.id}`}>Delete</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {pending.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[var(--accent)] mb-3 font-semibold">Pending Approval ({pending.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{pending.map(p => <Card key={p.id} p={p} />)}</div>
        </div>
      )}
      {others.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[var(--muted)] mb-3">All Other Promotions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{others.map(p => <Card key={p.id} p={p} />)}</div>
        </div>
      )}
    </div>
  );
}



function UserManagement({ items, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", role: "receptionist", email: "", username: "" });
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, role: form.role };
      if (form.email) payload.email = form.email;
      if (form.username) payload.username = form.username;
      const r = await api.post("/admin/users", payload);
      setCreated(r.data);
      setForm({ name: "", role: "receptionist", email: "", username: "" });
      onRefresh?.();
      toast.success("User created");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create user");
    }
  };

  const toggleActive = async (u) => {
    try {
      const newActive = u.active === false;
      await api.patch(`/admin/users/${u.id}/active`, { active: newActive });
      toast.success(newActive ? "Account activated" : "Account deactivated");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.role})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); onRefresh?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const roleBadge = (r) => {
    const map = { admin: "bg-purple-100 text-purple-800", owner: "bg-amber-100 text-amber-800", receptionist: "bg-blue-100 text-blue-800", agent: "bg-green-100 text-green-800" };
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${map[r] || "bg-gray-100"}`}>{r}</span>;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <p className="text-sm text-[var(--muted)] italic max-w-2xl">Manage all internal accounts. Create new admin, owner, reception, or agent staff. Deactivate accounts to instantly block login without deleting the audit trail.</p>
        <button onClick={() => setAdding(!adding)} className="btn-gold rounded-full px-5 py-2 text-xs tracking-widest uppercase" data-testid="add-user-toggle">{adding ? "Cancel" : "+ Create User"}</button>
      </div>
      {adding && (
        <form onSubmit={submit} className="bg-white border border-[var(--border)] p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-user-form">
          <input required placeholder="Full name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="admin-user-name" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <select value={form.role} onChange={e=>setForm({...form, role: e.target.value})} data-testid="admin-user-role" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5">
            <option value="receptionist">Reception</option>
            <option value="agent">Agent</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
          </select>
          <input placeholder="Username (optional)" value={form.username} onChange={e=>setForm({...form, username: e.target.value})} data-testid="admin-user-username" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <input type="email" placeholder="Email (optional)" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="admin-user-email" className="bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" />
          <button type="submit" className="md:col-span-2 btn-gold rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="admin-user-submit">Create Account</button>
        </form>
      )}
      {created && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full border border-[var(--border)]">
            <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
              <h3 className="font-display text-2xl">Account Created</h3>
              <button onClick={() => setCreated(null)} className="text-2xl">×</button>
            </div>
            <div className="p-7">
              <p className="text-sm mb-3">Share these credentials with the new user. They must change the password on first login.</p>
              <div className="bg-[var(--bg)] border border-[var(--border)] p-4 space-y-2 mb-4">
                <p className="text-xs text-[var(--muted)] uppercase tracking-widest">Username</p>
                <p className="font-medium">{created.username}</p>
                <p className="text-xs text-[var(--muted)] uppercase tracking-widest mt-3">Temporary Password</p>
                <p className="font-mono text-lg text-[var(--accent)]">{created.temp_password}</p>
              </div>
              <button onClick={() => setCreated(null)} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest">Done</button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border border-[var(--border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
            <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Username</th><th className="text-left p-4">Email</th><th className="text-left p-4">Role</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th></tr>
          </thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="border-t border-[var(--border)]" data-testid={`admin-user-${u.id}`}>
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4 text-xs">{u.username || "—"}</td>
                <td className="p-4 text-xs">{u.email || "—"}</td>
                <td className="p-4">{roleBadge(u.role)}</td>
                <td className="p-4 text-xs">{u.active === false ? <span className="text-red-600">Deactivated</span> : <span className="text-green-700">Active</span>}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => toggleActive(u)} className={`text-xs px-3 py-1.5 rounded border ${u.active === false ? "border-green-300 text-green-700 hover:bg-green-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`} data-testid={`admin-user-toggle-${u.id}`}>
                    {u.active === false ? "Activate" : "Deactivate"}
                  </button>
                  <button onClick={() => remove(u)} className="text-xs px-3 py-1.5 border border-red-300 text-red-600 rounded hover:bg-red-50" data-testid={`admin-user-delete-${u.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
